import Text from '@/components/common/Text';
import { Theme } from '@/styles/Theme';
import type { PaymentEventLog, PaymentEventType } from '@/types/admin.types';
import styled from '@emotion/styled';

interface PaymentEventLogSectionProps {
  events: PaymentEventLog[];
  title?: string;
}

const EVENT_TYPE_MAP: Record<PaymentEventType, { label: string; color: string; icon: string }> = {
  auto_charge_attempt: { label: '자동 결제 시도', color: '#2196F3', icon: '🔄' },
  auto_charge_success: { label: '자동 결제 성공', color: '#4CAF50', icon: '✅' },
  auto_charge_failed: { label: '자동 결제 실패', color: '#F44336', icon: '❌' },
  auto_charge_retry: { label: '결제 재시도', color: '#FF9800', icon: '🔁' },
  manual_payment_success: { label: '수동 결제 성공', color: '#4CAF50', icon: '💳' },
  admin_approve: { label: '관리자 승인', color: '#9C27B0', icon: '👤' },
  billing_key_updated: { label: '빌링키 업데이트', color: '#00BCD4', icon: '🔑' },
  billing_key_disabled: { label: '빌링키 비활성화', color: '#FF5722', icon: '🔒' },
  subscription_expired: { label: '구독 만료', color: '#F44336', icon: '⏰' },
  subscription_activated: { label: '구독 활성화', color: '#4CAF50', icon: '🟢' },
  workers_locked: { label: '작업자 잠금', color: '#F44336', icon: '🔴' },
  workers_unlocked: { label: '작업자 잠금 해제', color: '#4CAF50', icon: '🟢' },
};

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatAmount = (amount: number | null) => {
  if (amount === null || amount === 0) return null;
  return `${amount.toLocaleString('ko-KR')}원`;
};

const parseMetadata = (metadata: string | null): Record<string, unknown> | null => {
  if (!metadata) return null;
  try {
    return JSON.parse(metadata);
  } catch {
    return null;
  }
};

const PaymentEventLogSection = ({ events, title = '결제 이력' }: PaymentEventLogSectionProps) => {
  if (!events || events.length === 0) {
    return (
      <Container>
        <SectionHeader>
          <Text fontSize='1.125rem' fontWeight={700}>
            {title}
          </Text>
        </SectionHeader>
        <EmptyMessage>결제 이벤트가 없습니다.</EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeader>
        <Text fontSize='1.125rem' fontWeight={700}>
          {title}
        </Text>
        <EventCount>{events.length}건</EventCount>
      </SectionHeader>
      <Timeline>
        {events.map((event) => {
          const eventConfig = EVENT_TYPE_MAP[event.event_type] || {
            label: event.event_type,
            color: '#666',
            icon: '📋',
          };
          const metadata = parseMetadata(event.metadata);
          const amountStr = formatAmount(event.amount);

          return (
            <TimelineItem key={event.id}>
              <TimelineDot color={eventConfig.color} />
              <TimelineContent>
                <EventHeader>
                  <EventIcon>{eventConfig.icon}</EventIcon>
                  <EventLabel color={eventConfig.color}>{eventConfig.label}</EventLabel>
                  {event.retry_count > 0 && (
                    <RetryBadge>재시도 #{event.retry_count}</RetryBadge>
                  )}
                  <EventTime>{formatDateTime(event.created_at)}</EventTime>
                </EventHeader>

                <EventDetails>
                  {amountStr && <DetailItem>금액: {amountStr}</DetailItem>}
                  {event.pg_pay_rst && (
                    <DetailItem>
                      결과: <StatusText success={event.pg_pay_rst === 'success'}>{event.pg_pay_rst}</StatusText>
                    </DetailItem>
                  )}
                  {event.pg_pay_msg && <DetailItem>메시지: {event.pg_pay_msg}</DetailItem>}
                  {event.pg_pay_code && <DetailItem>코드: {event.pg_pay_code}</DetailItem>}
                  {event.pg_card_name && <DetailItem>카드: {event.pg_card_name} {event.pg_card_num || ''}</DetailItem>}
                  {event.pg_pay_oid && <DetailItem>주문번호: <OidText>{event.pg_pay_oid}</OidText></DetailItem>}
                  {metadata && Object.keys(metadata).length > 0 && (
                    <MetadataBox>
                      {Object.entries(metadata).map(([key, value]) => (
                        <MetadataItem key={key}>
                          <MetadataKey>{key}:</MetadataKey> {String(value)}
                        </MetadataItem>
                      ))}
                    </MetadataBox>
                  )}
                </EventDetails>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Container>
  );
};

const Container = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 0.0625rem solid ${Theme.colors.G_4};
  margin-bottom: 1rem;
`;

const EventCount = styled.span`
  font-size: 0.875rem;
  color: ${Theme.colors.G_2};
  background: ${Theme.colors.G_4};
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: ${Theme.colors.G_2};
  padding: 2rem;
  font-size: 0.875rem;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 0.5rem;
    top: 0;
    bottom: 0;
    width: 0.125rem;
    background: ${Theme.colors.G_4};
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 1.25rem;
  display: flex;
  gap: 0.75rem;

  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineDot = styled.div<{ color: string }>`
  position: absolute;
  left: -1.25rem;
  top: 0.25rem;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background: ${(props) => props.color};
  z-index: 1;
`;

const TimelineContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EventIcon = styled.span`
  font-size: 1rem;
`;

const EventLabel = styled.span<{ color: string }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.color};
`;

const RetryBadge = styled.span`
  font-size: 0.75rem;
  background: #FF9800;
  color: white;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-weight: 600;
`;

const EventTime = styled.span`
  font-size: 0.75rem;
  color: ${Theme.colors.G_2};
  margin-left: auto;
`;

const EventDetails = styled.div`
  margin-top: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const DetailItem = styled.span`
  font-size: 0.8125rem;
  color: ${Theme.colors.G_1};
`;

const StatusText = styled.span<{ success: boolean }>`
  color: ${(props) => (props.success ? '#4CAF50' : '#F44336')};
  font-weight: 600;
`;

const OidText = styled.code`
  font-size: 0.75rem;
  background: ${Theme.colors.G_4};
  padding: 0.0625rem 0.25rem;
  border-radius: 0.125rem;
`;

const MetadataBox = styled.div`
  margin-top: 0.25rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.25rem;
  border-left: 0.1875rem solid ${Theme.colors.G_3};
`;

const MetadataItem = styled.div`
  font-size: 0.75rem;
  color: ${Theme.colors.G_2};
  line-height: 1.4;
`;

const MetadataKey = styled.span`
  font-weight: 600;
  color: ${Theme.colors.G_1};
`;

export default PaymentEventLogSection;
